import { createApp } from "vue";
import AudioRecorder from "../../../audio-recorder/components/AudioRecorder.vue";
import richTextUtils from "../../utils/richtext.utils";
import UgcFileService from "../../service/ugc.file.service";
import DOMUtils from "@utils/dom.utils";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import i18n from "@plugins/i18n.ts";
import { isEmpty } from "@utils/var.check";
import { RECORDING_MAX_DURATION } from "@modules/attempt/service/attempt.constant";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import recorderEventBus from "@plugins/audio-recorder/service/event-bus";
import FileUploaderHidden from "@plugins/file-uploader-v2/components/FileUploaderHidden.vue";
import { FileResponse, FileStatus, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import { MEDIA_TYPE } from "@modules/editor/service/editor.constant";
import { MEDIA_RECORD_TYPE } from "@/plugins/media/media.constant";

// plugin name: recording

export default function (editor) {
  const MEDIA_SELECTOR = "div.sls-media[data-type='ugc']";
  const RE_RENDER_MEDIA_SELECTOR =
    "div.paragraph > div.sls-media-wrapper > div.sls-media-tag[data-type='ugc']";

  const richTextEditor = editor.options.get("richTextEditor");
  let fileService = null;
  let cleanupRecordingUpload: (() => void) | null = null;

  // plugin configurations
  const configs =
    !isEmpty(richTextEditor.config.plugins) && !isEmpty(richTextEditor.config.plugins["recording"])
      ? richTextEditor.config.plugins["recording"]
      : { maxDurationInMilliSeconds: RECORDING_MAX_DURATION.DEFAULT }; // default 10 minutes

  async function handleRecordingSaved(file: File) {
    const fileEntry = {
      file: file,
    };

    // Step 1: Create the UGC record first (without fileUuid — file not uploaded yet).
    // This gives us the ugcUuid to pass into generate-scan-url so Valkey stores
    // resourceType="UGC" + resourceUuid=ugcUuid from the start.
    // The actual file move (resource bucket → UGC bucket) will be done by
    // FileMoveJobListenerImpl on the BE after the scan completes.
    try {
      const ugcUuid = await fileService.preCreate();
      cleanupRecordingUpload?.();

      // The shared modal can unmount FileUploaderHidden when another modal opens, preventing its
      // READY or FAILED result from reaching the recorder. A standalone app keeps the upload alive;
      // createApp listeners use Vue's on* prop convention.
      const container = document.createElement("div");
      document.body.appendChild(container);

      const cleanupUpload = () => {
        uploadApp.unmount();
        container.remove();
        if (cleanupRecordingUpload === cleanupUpload) {
          cleanupRecordingUpload = null;
        }
      };

      const uploadApp = createApp(FileUploaderHidden, {
        params: {
          uploadFlow: UploadFlow.AUDIO,
          fileEntry,
          recordType: MEDIA_RECORD_TYPE.UGC,
          recordUuid: ugcUuid,
        },
        "onUpdate:fileEntry": (uploadedResult: FileResponse) => {
          if (uploadedResult?.status !== FileStatus.READY || !uploadedResult.uuid) {
            // Notify the recorder before destroying the hidden uploader so the error state is not
            // lost while the standalone upload app is being cleaned up.
            recorderEventBus.$emit("recordingSavedFailed", uploadedResult);
            cleanupUpload();
            return;
          }

          cleanupUpload();

          // UGC record already created — just insert into editor
          richTextEditor.attachedFiles.push({
            uuid: ugcUuid,
            file: file,
          });

          richTextUtils.insertInlineAttachment(editor, {
            uuid: ugcUuid,
            type: fileService.getType(),
            mediaType: MEDIA_TYPE.AUDIO,
            fileName: uploadedResult.name,
          });
          renderInlineMedia(editor, ".inline-placeholder");
          editor.save();
          richTextEditor.$stickpanel.remove();
          richTextEditor.handleUpdateRecordStatus(false);
        },
      });

      AppInstanceUtils.installCommonPlugin(uploadApp);
      cleanupRecordingUpload = cleanupUpload;
      uploadApp.mount(container);
    } catch (error) {
      cleanupRecordingUpload?.();
      console.error("Failed to pre-create UGC record:", error);
      recorderEventBus.$emit("recordingSavedFailed", error);
    }
  }
  editor.ui.registry.addButton("recording", {
    tooltip: i18n.t("tinymce_plugin_media_recording_button_tooltip"),
    icon: "embed-recording",
    onAction: () => {
      if (!richTextEditor.$stickpanel.hasActiveComponent()) {
        startAudioRecording();
      } else {
        // prompt to discard current recording
        const props = {
          title: i18n.t("audio_recorder_message_discard_ongoing_confirmation_title"),
          content: i18n.t("audio_recorder_message_discard_ongoing_confirmation_content"),
          primaryBtnLabel: i18n.t("button_ok"),
          secondaryBtnLabel: i18n.t("button_cancel"),
          actionPrimary: () => {
            richTextEditor.$stickpanel.remove(); // remove top/current sticky content
            richTextEditor.$nextTick(() => {
              // restart audio recording after current active audio recorder component is destroyed successfully
              startAudioRecording();
            });
          },
        };
        richTextEditor.$modal.showMessage("warning", props, true);
      }
    },
  });

  (function init() {
    if (
      richTextEditor.config &&
      richTextEditor.config.plugins &&
      richTextEditor.config.plugins["fileUpload"] &&
      isEmpty(richTextEditor.config.plugins["recording"])
    ) {
      richTextEditor.plugins["fileUpload"] = {
        renderInEditor,
        cleanUp,
        containPluginElements,
        startAudioRecording,
        destroy,
      };
      const serviceConfig = richTextEditor.config.plugins.fileUpload.fileServiceConfig;
      if (serviceConfig.type === "ugc") {
        fileService = new UgcFileService(serviceConfig);
      } else if (serviceConfig.type === "mo") {
        // init media object file service
      }
    } else if (
      richTextEditor.config &&
      richTextEditor.config.plugins &&
      richTextEditor.config.plugins["recording"]
    ) {
      richTextEditor.plugins["recording"] = {
        renderInEditor,
        cleanUp,
        containPluginElements,
        startAudioRecording,
        destroy,
      };
      const serviceConfig = richTextEditor.config.plugins.recording.fileServiceConfig;
      if (serviceConfig.type === "ugc") {
        fileService = new UgcFileService(serviceConfig);
      } else if (serviceConfig.type === "mo") {
        // init media object file service
      }
    }
  })();
  function startAudioRecording() {
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(recorderEventBus.$on("recordingSaved", handleRecordingSaved));
    eventBusWatchInstances.push(
      recorderEventBus.$on("isRecording", richTextEditor.handleUpdateRecordStatus),
    );
    richTextEditor.$stickpanel.set(AudioRecorder, {
      kind: richTextEditor.kind,
      allowUpload: richTextEditor.allowUpload,
      maxSliceOfDuration: configs.maxDurationInMilliSeconds,
      eventBusWatchInstances: eventBusWatchInstances,
    });
  }

  function renderInEditor(rerender = false) {
    if (rerender) {
      richTextUtils.renderInlineMedia(editor, RE_RENDER_MEDIA_SELECTOR);
    } else if (richTextEditor.config.plugins["recording"]) {
      richTextUtils.renderInlineMedia(editor, MEDIA_SELECTOR);
    } else {
      renderInlineMedia(editor, MEDIA_SELECTOR);
    }
  }

  function renderInlineMedia(editor, selector) {
    if (!editor.getBody()) return;
    const mediaList = editor.options.get("mediaList");
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      const propsData = {
        uuid: element.getAttribute("data-uuid"),
        width: element.getAttribute("data-width"),
        height: element.getAttribute("data-height"),
        cuePoints: {
          start: element.getAttribute("data-cue-start"),
          end: element.getAttribute("data-cue-end"),
        },
        isTranscriptEnabled: element.getAttribute("data-transcript-enabled"),
        transcriptionFileVersion: element.getAttribute("transcription-file-version"),
        type: "ugc",
        isInline: true,
      };

      const mediaType = element.getAttribute("media-type");
      propsData.mediatype = mediaType?.toLowerCase().trim();
      propsData.fileName = element.getAttribute("data-file-name");
      propsData.isUgc = true;

      // wrap a layer with non editable
      const wrapper = DOMUtils.createElement("div", {
        class: "sls-media-wrapper",
        contenteditable: false,
      });
      element.parentNode?.insertBefore(wrapper, element);
      wrapper.appendChild(element);

      const slsMedia = richTextUtils.createInlineUgcMediaTag({
        uuid: element.getAttribute("data-uuid"),
        type: mediaType,
      });
      DOMUtils.removeClass(slsMedia, "sls-media");
      DOMUtils.addClass(slsMedia, "sls-media-tag");

      const mediaApp = createApp(MediaComponent, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        ...propsData,
      });
      AppInstanceUtils.installCommonPlugin(mediaApp);
      // Mount media component to media element, include the media tag
      const mediaComponent = mediaApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);

      mediaComponent.$el.parentNode.appendChild(slsMedia);

      mediaList.push(mediaComponent);

      // add <br> if last item is media attachment, so user can add cursor after
      const mediaWrapper = mediaComponent.$el.closest(".sls-media-wrapper");
      const parentParagraph = mediaWrapper.closest("div.paragraph");
      if (parentParagraph && !parentParagraph.nextSibling) {
        // let lineElement = document.createElement("br");
        // lineElement.setAttribute("data-mce-bogus", 1);
        const paragraph = DOMUtils.createElement("div", { class: "paragraph" });
        paragraph.innerHTML = "&nbsp;";
        parentParagraph.parentNode.insertBefore(paragraph, parentParagraph.nextSibling);
      }
    });
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });

    DOMUtils.elOf(container, ".sls-media[data-type='ugc']").forEach((element) => {
      const uuid = element.getAttribute("data-uuid");
      const media = DOMUtils.elOf(containerAfterCleanUp, `.sls-media[data-uuid='${uuid}']`)[0];
      const mediaWrapper = media.closest(".sls-media-wrapper");
      if (mediaWrapper) {
        // case when clean up has been done by other plugin
        mediaWrapper.replaceWith(element);
      }
    });

    DOMUtils.elOf(container, ".sls-media-tag[data-type='ugc']").forEach((element) => {
      const uuid = element.getAttribute("data-uuid");
      const media = DOMUtils.elOf(containerAfterCleanUp, `.sls-media-tag[data-uuid='${uuid}']`)[0];
      const mediaWrapper = media.closest(".sls-media-wrapper");

      // switch back the class
      DOMUtils.removeClass(element, "sls-media-tag");
      DOMUtils.addClass(element, "sls-media");
      mediaWrapper?.replaceWith(element);
    });

    return containerAfterCleanUp.innerHTML;
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-media, .sls-media-object, figure.media").length > 0;
  }

  function destroy() {
    cleanupRecordingUpload?.();
    richTextEditor.$stickpanel.remove();
  }
}
